import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import PlayerTeamModal from '../components/PlayerTeamModal';
import { Trophy, Medal, Car, Bike, BookOpen, Calendar, Zap } from 'lucide-react';
import RoundResultsAccordion from '../components/RoundResultsAccordion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Race Calendar Data
const RACE_CALENDAR_UNSORTED = [
  { round: 1, dates: '16-17 May', month: 'May' },
  { round: 2, dates: '4-5 July', month: 'July' },
  { round: 3, dates: '29-30 August', month: 'August' },
  { round: 4, dates: '17-18 October', month: 'October' },
  { round: 5, dates: '5-6 December', month: 'December' },
];
const RACE_CALENDAR = [...RACE_CALENDAR_UNSORTED].sort((a, b) => a.round - b.round);

export default function Leaderboard() {
  const [user, setUser] = useState(null);
  const [racingLeaderboard, setRacingLeaderboard] = useState([]);
  const [motoLeaderboard, setMotoLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('racing');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, racingRes, motoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leaderboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/leaderboard`, { withCredentials: true }),
      ]);

      setUser(userRes.data);
      setRacingLeaderboard(racingRes.data);
      setMotoLeaderboard(motoRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load leaderboard');
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy className="text-yellow-400" size={24} />;
    if (rank === 2) return <Medal className="text-gray-400" size={24} />;
    if (rank === 3) return <Medal className="text-orange-400" size={24} />;
    return <span className="font-mono text-xl font-bold text-zinc-500">#{rank}</span>;
  };

  const leaderboard = activeTab === 'racing' ? racingLeaderboard : motoLeaderboard;
  const accentColor = activeTab === 'racing' ? 'primary' : 'secondary';

  return (
    <PageBackground pageKey="leaderboard">
    <div className="min-h-screen">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="leaderboard-title">
            <span className={`text-${accentColor}`}>LEADERBOARD</span>
          </h1>
          <p className="text-zinc-400">See how you stack up against the competition</p>
        </div>

        {/* Series Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTab('racing')}
            data-testid="leaderboard-racing-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'racing'
                ? 'bg-primary text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Car size={20} />
            <MSFLogo suffix="Racing" size="sm" />
          </button>
          <button
            onClick={() => setActiveTab('moto')}
            data-testid="leaderboard-moto-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'moto'
                ? 'bg-secondary text-black'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bike size={20} />
            <MSFLogo suffix="" size="sm" />
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            data-testid="leaderboard-rules-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'rules'
                ? 'bg-yellow-500 text-black'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <BookOpen size={20} />
            <span>Rules</span>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            data-testid="leaderboard-calendar-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'calendar'
                ? 'bg-blue-500 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Calendar size={20} />
            <span>Calendar</span>
          </button>
        </div>

        {/* Rules Tab Content */}
        {activeTab === 'rules' && (
          <div className="bg-card border border-zinc-800 p-8" data-testid="leaderboard-rules-content">
            <h2 className="font-heading font-bold text-3xl mb-6">
              <span className="text-yellow-500">RULES</span> & SCORING
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-heading font-bold text-xl text-primary mb-3">Team Selection</h3>
                <ul className="space-y-2 text-zinc-300">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary mt-2 mr-3"></span>
                    <span><strong><MSFLogo suffix="Racing" size="xs" />:</strong> Select 2 Drivers, 3 Cars, and 1 Tuner within 200,000 credits</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-secondary mt-2 mr-3"></span>
                    <span><strong><MSFLogo suffix="" size="xs" />:</strong> Select 2 Riders, 3 Bikes, and 1 Tuner within 150,000 credits</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-yellow-500 mt-2 mr-3"></span>
                    <span>You can participate in both leagues simultaneously!</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 mt-2 mr-3"></span>
                    <span><strong>Transfer Window:</strong> Players are only allowed to change their current team lineup from the 6th of July to the 29th of August. Other than that, team lineup changes are allowed throughout the season.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-heading font-bold text-xl text-primary mb-3">Points System</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { pos: '1st', pts: 100 },
                    { pos: '2nd', pts: 85 },
                    { pos: '3rd', pts: 75 },
                    { pos: '4th', pts: 65 },
                    { pos: '5th', pts: 60 },
                    { pos: '6th', pts: 55 },
                    { pos: '7th', pts: 50 },
                    { pos: '8th', pts: 45 },
                    { pos: '9th', pts: 40 },
                    { pos: '10th', pts: 35 },
                    { pos: '11th', pts: 30 },
                    { pos: '12th', pts: 25 },
                    { pos: '13th', pts: 20 },
                    { pos: '14th', pts: 10 },
                    { pos: '15th', pts: 5 },
                  ].map((item) => (
                    <div key={item.pos} className="bg-zinc-900 border border-zinc-800 p-3 text-center">
                      <p className="text-zinc-400 text-sm">{item.pos}</p>
                      <p className="font-mono font-bold text-xl text-secondary">{item.pts}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-heading font-bold text-xl text-red-500 mb-3">Penalties</h3>
                <div className="bg-red-900/20 border border-red-800 p-4">
                  <p className="text-red-300">
                    <strong>-50 points</strong> penalty for changing your team selection after the initial lock-in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab Content */}
        {activeTab === 'calendar' && (
          <div className="bg-card border border-zinc-800 p-8" data-testid="leaderboard-calendar-content">
            <h2 className="font-heading font-bold text-3xl mb-6">
              <span className="text-blue-500">RACE</span> CALENDAR 2025
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {RACE_CALENDAR.map((race) => (
                <div 
                  key={race.round} 
                  className="bg-zinc-900 border-2 border-zinc-800 hover:border-blue-500 transition-colors p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-500 text-white px-3 py-1 font-heading font-bold text-sm">
                      ROUND {race.round}
                    </span>
                    <span className="text-zinc-500 text-sm">{race.month}</span>
                  </div>
                  <p className="font-heading font-bold text-2xl text-white">{race.dates}</p>
                  <p className="text-zinc-400 mt-2">Sepang International Circuit</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-zinc-900 border border-zinc-700 p-4 text-center">
              <p className="text-zinc-400">
                Make sure to lock in your teams before playing!
              </p>
            </div>
          </div>
        )}

        {/* Leaderboard Table - Only show for racing/moto tabs (collapsible) */}
        {(activeTab === 'racing' || activeTab === 'moto') && (
        <Accordion type="multiple" defaultValue={['overall-standings']} className="space-y-2">
          <AccordionItem value="overall-standings" className="bg-card border border-zinc-800">
            <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="overall-standings-trigger">
              <div className="flex items-center justify-between w-full pr-4">
                <div className="text-left">
                  <span className={`bg-${accentColor} text-black px-2 py-0.5 font-heading font-bold text-xs`}>
                    OVERALL
                  </span>
                  <span className="ml-3 font-heading font-bold text-lg">Current Standings</span>
                </div>
                <span className="text-xs text-zinc-500 hidden sm:block">
                  {leaderboard.length} players
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-0 pb-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">RANK</th>
                      <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">PLAYER</th>
                      <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">POINTS</th>
                      <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">PENALTY</th>
                    </tr>
                  </thead>
                  <tbody>
                {leaderboard.map((entry, idx) => {
                  const isCurrentUser = entry.user.user_id === user?.user_id;
                  return (
                    <tr
                      key={entry.user.user_id}
                      data-testid={`leaderboard-row-${idx}`}
                      className={`border-b border-zinc-800 ${
                        isCurrentUser ? (activeTab === 'racing' ? 'bg-primary/10' : 'bg-secondary/10') : 'hover:bg-zinc-900'
                      } transition-colors`}
                    >
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          {getRankBadge(entry.rank)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          {entry.user.picture ? (
                            <img
                              src={entry.user.picture}
                              alt={entry.user.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center font-bold">
                              {entry.user.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <button
                              onClick={() => setSelectedPlayer({ userId: entry.user.user_id, userName: entry.user.name })}
                              className={`font-bold hover:text-${accentColor} hover:underline transition-colors text-left`}
                              data-testid={`view-team-${entry.user.user_id}`}
                            >
                              {entry.user.name}
                              {isCurrentUser && (
                                <span className={`ml-2 text-xs text-${accentColor} font-mono`}>(YOU)</span>
                              )}
                              {entry.active_boost_count > 0 && (
                                <span
                                  className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/40`}
                                  title={`${entry.active_boost_count} active boost${entry.active_boost_count > 1 ? 's' : ''}`}
                                  data-testid={`boost-badge-${entry.user.user_id}`}
                                >
                                  <Zap size={10} className="fill-yellow-400" />
                                  {entry.active_boost_count}
                                </span>
                              )}
                            </button>
                            {entry.team_name && (
                              <p className={`text-xs text-${accentColor} font-heading font-bold`}>{entry.team_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className={`font-mono text-2xl font-bold ${activeTab === 'racing' ? 'text-secondary' : 'text-primary'}`}>
                          {entry.total_points}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-mono text-lg text-red-500">
                          {entry.penalty_points > 0 ? `-${entry.penalty_points}` : '0'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {leaderboard.length === 0 && (
            <div className="p-12 text-center" data-testid="empty-leaderboard">
              <p className="text-zinc-400 text-xl">No players on the {activeTab === 'racing' ? 'Racing' : 'MotoMSF'} leaderboard yet!</p>
              <p className="text-zinc-500 mt-2">Be the first to select your team.</p>
            </div>
          )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        )}

        {/* Historical Round Results (accordion) */}
        {(activeTab === 'racing' || activeTab === 'moto') && (
          <div className="mt-6">
            <RoundResultsAccordion
              league={activeTab}
              currentUserId={user?.user_id}
              accentColor={accentColor}
            />
          </div>
        )}
      </div>

      {/* Player Team Modal */}
      {selectedPlayer && (
        <PlayerTeamModal
          userId={selectedPlayer.userId}
          userName={selectedPlayer.userName}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
    </PageBackground>
  );
}
